import { useEffect, useState } from "react";
import { FolderPlusIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import useForm from "../../../../hooks/useForm";
import AccessoryTable from "./AccessoryTable";
import AddAccModal from "./AddAccModal";
import RemoveAccModal from "./RemoveAccModal";
import { toast } from "react-toastify";
import {
  useDeleteJobAccessoryMutation,
  useUpdateJobAccessoriesMutation,
} from "../../../../redux/services/jobs/jobsApi";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/access-control";
import { Button, Card } from "../../../../shared";
import { getProductTerminology } from "../../../../constants/product-terminology";

const Accessories = ({ user, jobId, accessories, type, appType }) => {
  const { hasPermission } = usePermissions();
  const canEditJobs = hasPermission(PERMISSIONS.JOBS_EDIT);
  const terms = getProductTerminology(appType);
  const jobLower = terms.jobLower || "job";
  const [data, setData] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);

  const [updateAccessory, { isLoading: updatingAccessory }] =
    useUpdateJobAccessoriesMutation();
  const [deleteAccessory, { isLoading: deletingAccessory }] =
    useDeleteJobAccessoryMutation();

  const form = useForm();

  useEffect(() => {
    if (accessories && accessories.length > 0) {
      setData(accessories);
    }

    if (accessories && accessories.length === 0) {
      setData([]);
    }
  }, [accessories]);

  async function handleAddAccessory() {
    const data = {
      type: form.form.type,
      quantity: form.form.quantity,
      unitPrice: form.form.unitPrice,
      jobId,
    };

    let method = "post";
    if (form.form.edit) {
      method = "put";
      data._id = form.form._id;
    }

    const res = await updateAccessory({ method, data }).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: "added-accessory-" + res.data._id,
    });

    if (res.status === "success") {
      form.clearForm();
      setAddModalVisible(false);
    }
  }

  function editAcc(e) {
    form.setFormInit({ edit: true, ...e });
    setAddModalVisible(true);
  }

  function removeAcc(e) {
    form.setFormInit(e);
    setRemoveModalVisible(true);
  }

  async function handleRemoveAccessory() {
    const res = await deleteAccessory({
      data: {
        jobId,
        _id: form.form._id,
      },
    }).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: "added-accessory-" + res.data._id,
    });

    if (res.status === "success") {
      setRemoveModalVisible(false);
      form.clearForm();
    }
  }

  function handleCancel() {
    form.clearForm();
  }

  return (
    <>
      <Card
        className="row-span-2 pt-2"
        header={
          <div className="bg-white border border-b-0 border-gray-200 rounded-t-lg px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800">
                  Accessories{` (${data?.length || 0})`}
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  Parts, materials, or miscellaneous charges associated with
                  this {jobLower}.
                </div>
              </div>
              {canEditJobs && (
                <div className="shrink-0">
                  <Button
                    type="primary"
                    size="sm"
                    icon={<PlusCircleIcon className="h-5 w-5 mr-2" />}
                    onClick={() => setAddModalVisible(true)}
                  >
                    Add Accessory
                  </Button>
                </div>
              )}
            </div>
          </div>
        }
      >
        <div className="px-5 pb-5">
          {!data || data.length === 0 ? (
            <div className="py-10">
              <div className="text-center">
                <FolderPlusIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
                <h3 className="mt-2 font-semibold text-sm">No Accessories</h3>
                <p className="text-sm text-gray-500">
                  Get started by adding an accessory to this {jobLower}.
                </p>
                {canEditJobs && (
                  <div className="mt-4">
                    <Button
                      type="primary"
                      size="sm"
                      icon={<PlusCircleIcon className="h-5 w-5 mr-2" />}
                      onClick={() => setAddModalVisible(true)}
                    >
                      Add Accessory
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <AccessoryTable
              data={data}
              editAcc={editAcc}
              removeAcc={removeAcc}
              jobLower={jobLower}
            />
          )}
        </div>
      </Card>
      <AddAccModal
        appType={appType}
        form={form}
        visible={addModalVisible}
        setVisible={setAddModalVisible}
        submit={handleAddAccessory}
        loading={updatingAccessory}
        cancel={handleCancel}
      />
      <RemoveAccModal
        form={form}
        visible={removeModalVisible}
        setVisible={setRemoveModalVisible}
        submit={handleRemoveAccessory}
        loading={deletingAccessory}
        cancel={handleCancel}
      />
    </>
  );
};

export default Accessories;
